import React, { Component } from 'react';
import { motion } from 'framer-motion';
import { RentCertificate } from '../../types';
import { ShieldCheck, Calendar, MapPin, Download, Share2, Printer, CheckCircle, Building, User, Phone, Mail, FileText, Stamp } from 'lucide-react';
interface CertificateCardProps {
  certificate: RentCertificate;
}
export function CertificateCard({
  certificate
}: CertificateCardProps) {
  // Generate QR code URL using a QR code API
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(`https://renta.ng/verify/${certificate.id}`)}`;
  return <motion.div initial={{
    opacity: 0,
    scale: 0.95,
    y: 20
  }} animate={{
    opacity: 1,
    scale: 1,
    y: 0
  }} transition={{
    duration: 0.8,
    ease: 'easeOut'
  }} className="relative max-w-4xl mx-auto perspective-1000" data-id="element-613">
      {/* Glow Effect */}
      <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 rounded-xl opacity-20 blur-xl animate-pulse" data-id="element-614"></div>

      <div className="bg-[#FFFAF0] rounded-xl shadow-2xl border-8 border-double border-[#B8860B]/30 overflow-hidden relative" data-id="element-615">
        {/* Guilloche Pattern Background */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
        backgroundImage: `radial-gradient(circle at center, #000 1px, transparent 1px), radial-gradient(circle at center, #000 1px, transparent 1px)`,
        backgroundSize: '20px 20px',
        backgroundPosition: '0 0, 10px 10px'
      }} data-id="element-616"></div>

        {/* Corner Flourishes */}
        <div className="absolute top-0 left-0 w-32 h-32 border-t-4 border-l-4 border-[#B8860B]/40 rounded-tl-3xl m-4" data-id="element-617"></div>
        <div className="absolute top-0 right-0 w-32 h-32 border-t-4 border-r-4 border-[#B8860B]/40 rounded-tr-3xl m-4" data-id="element-618"></div>
        <div className="absolute bottom-0 left-0 w-32 h-32 border-b-4 border-l-4 border-[#B8860B]/40 rounded-bl-3xl m-4" data-id="element-619"></div>
        <div className="absolute bottom-0 right-0 w-32 h-32 border-b-4 border-r-4 border-[#B8860B]/40 rounded-br-3xl m-4" data-id="element-620"></div>

        <div className="p-12 relative z-10" data-id="element-621">
          {/* Header Section */}
          <div className="text-center mb-12" data-id="element-622">
            <div className="flex justify-center mb-6" data-id="element-623">
              <div className="w-24 h-24 bg-gradient-to-br from-blue-900 to-blue-700 rounded-full flex items-center justify-center shadow-lg border-4 border-[#B8860B]/50" data-id="element-624">
                <img src="https://cdn.magicpatterns.com/uploads/nEUSW8VBjg336ZuLftpftq/RENTA_(1).png" alt="Renta Seal" className="w-14 h-14 object-contain brightness-0 invert" data-id="element-625" />
              </div>
            </div>
            <h1 className="text-5xl font-serif font-bold text-gray-900 mb-2 tracking-wide" data-id="element-626">
              Rent Guarantee Certificate
            </h1>
            <div className="flex items-center justify-center space-x-4" data-id="element-627">
              <div className="h-px w-24 bg-gradient-to-r from-transparent via-[#B8860B] to-transparent" data-id="element-628"></div>
              <p className="text-[#B8860B] font-serif italic text-lg" data-id="element-629">
                Official Proof of Address & Tenancy
              </p>
              <div className="h-px w-24 bg-gradient-to-r from-transparent via-[#B8860B] to-transparent" data-id="element-630"></div>
            </div>
          </div>

          {/* Verification Badge */}
          <div className="flex justify-center mb-8" data-id="element-631">
            <div className="bg-green-50 border-2 border-green-500 rounded-full px-6 py-2 flex items-center space-x-2" data-id="element-632">
              <CheckCircle className="w-5 h-5 text-green-600" data-id="element-633" />
              <span className="text-green-800 font-bold text-sm" data-id="element-634">
                VERIFIED & GUARANTEED
              </span>
            </div>
          </div>

          {/* Main Content */}
          <div className="space-y-8 mb-12" data-id="element-635">
            <p className="text-gray-500 font-serif text-lg italic text-center" data-id="element-636">
              This certificate confirms that
            </p>

            <div className="relative inline-block w-full text-center" data-id="element-637">
              <h2 className="text-4xl md:text-6xl font-serif font-bold text-blue-900 px-8 py-2 border-b-2 border-[#B8860B]/20 inline-block" data-id="element-638">
                {certificate.tenantName}
              </h2>
            </div>

            <p className="text-gray-600 font-serif text-xl max-w-2xl mx-auto leading-relaxed text-center" data-id="element-639">
              is a verified tenant with active rent guarantee coverage through
              Renta Platforms Ltd. This document serves as official proof of
              address and tenancy for the property located at:
            </p>

            <div className="bg-white/50 backdrop-blur-sm border-2 border-[#B8860B]/30 rounded-lg p-6 max-w-3xl mx-auto" data-id="element-640">
              <div className="flex items-start justify-center text-gray-800 font-serif text-2xl" data-id="element-641">
                <MapPin className="w-6 h-6 text-[#B8860B] mr-3 mt-1 flex-shrink-0" data-id="element-642" />
                <span className="text-center" data-id="element-643">
                  {certificate.propertyAddress}
                </span>
              </div>
            </div>

            {/* Tenant Details for Verification */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-3xl mx-auto" data-id="element-644">
              <h3 className="text-sm font-bold text-blue-900 uppercase tracking-wide mb-4 text-center" data-id="element-645">
                Tenant Information (For Verification Purposes)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm" data-id="element-646">
                <div className="flex items-center" data-id="element-647">
                  <User className="w-4 h-4 text-blue-600 mr-2" data-id="element-648" />
                  <span className="text-gray-600" data-id="element-649">Full Name:</span>
                  <span className="ml-2 font-semibold text-gray-900" data-id="element-650">
                    {certificate.tenantName}
                  </span>
                </div>
                <div className="flex items-center" data-id="element-651">
                  <FileText className="w-4 h-4 text-blue-600 mr-2" data-id="element-652" />
                  <span className="text-gray-600" data-id="element-653">Certificate ID:</span>
                  <span className="ml-2 font-mono font-semibold text-gray-900" data-id="element-654">
                    {certificate.id}
                  </span>
                </div>
                <div className="flex items-center" data-id="element-655">
                  <Building className="w-4 h-4 text-blue-600 mr-2" data-id="element-656" />
                  <span className="text-gray-600" data-id="element-657">Property Type:</span>
                  <span className="ml-2 font-semibold text-gray-900" data-id="element-658">
                    Residential
                  </span>
                </div>
                <div className="flex items-center" data-id="element-659">
                  <Stamp className="w-4 h-4 text-blue-600 mr-2" data-id="element-660" />
                  <span className="text-gray-600" data-id="element-661">Status:</span>
                  <span className="ml-2 font-semibold text-green-600 uppercase" data-id="element-662">
                    {certificate.status}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex justify-center items-center space-x-12 text-gray-600" data-id="element-663">
              <div className="text-center" data-id="element-664">
                <p className="text-xs uppercase tracking-widest text-gray-400 mb-1" data-id="element-665">
                  Coverage Start
                </p>
                <p className="font-serif text-xl font-bold text-gray-900" data-id="element-666">
                  {new Date(certificate.coverageStartDate).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
                </p>
              </div>
              <div className="h-12 w-px bg-[#B8860B]/30" data-id="element-667"></div>
              <div className="text-center" data-id="element-668">
                <p className="text-xs uppercase tracking-widest text-gray-400 mb-1" data-id="element-669">
                  Coverage End
                </p>
                <p className="font-serif text-xl font-bold text-gray-900" data-id="element-670">
                  {new Date(certificate.coverageEndDate).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
                </p>
              </div>
            </div>
          </div>

          {/* Official Statement */}
          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-8 max-w-3xl mx-auto" data-id="element-671">
            <p className="text-sm text-gray-700 leading-relaxed" data-id="element-672">
              <strong className="text-yellow-900" data-id="element-673">Official Statement:</strong>{' '}
              This certificate is issued by Renta Platforms Ltd and confirms
              that the named tenant has active rent guarantee coverage. This
              document is legally valid for proof of address purposes and can be
              used for bank account opening, visa applications, utility
              connections, and other official purposes requiring address
              verification.
            </p>
          </div>

          {/* Footer Section */}
          <div className="grid grid-cols-3 gap-8 items-end border-t border-[#B8860B]/20 pt-8" data-id="element-674">
            <div className="text-center" data-id="element-675">
              <div className="w-32 h-32 mx-auto mb-2 relative bg-white p-2 rounded-lg border-4 border-gray-200 shadow-md" data-id="element-676">
                <img src={qrCodeUrl} alt="QR Code for Verification" className="w-full h-full object-contain" onError={e => {
                // Fallback if QR code fails to load
                e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect width="200" height="200" fill="%23f3f4f6"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="monospace" font-size="12" fill="%236b7280"%3EQR Code%3C/text%3E%3C/svg%3E';
              }} data-id="element-677" />
                <div className="absolute -bottom-2 -right-2 bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm flex items-center" data-id="element-678">
                  <CheckCircle className="w-3 h-3 mr-1" data-id="element-679" /> VERIFIED
                </div>
              </div>
              <p className="text-[10px] text-gray-400 uppercase tracking-widest" data-id="element-680">
                Scan to Authenticate
              </p>
              <p className="text-[8px] text-gray-400 mt-1" data-id="element-681">
                Verify at renta.ng/verify
              </p>
            </div>

            <div className="text-center" data-id="element-682">
              <div className="w-24 h-24 mx-auto mb-4 rounded-full border-4 border-[#B8860B]/30 flex items-center justify-center relative" data-id="element-683">
                <div className="absolute inset-1 border border-[#B8860B]/20 rounded-full" data-id="element-684"></div>
                <ShieldCheck className="w-12 h-12 text-[#B8860B]" data-id="element-685" />
              </div>
              <p className="text-[#B8860B] font-bold text-xs tracking-widest uppercase" data-id="element-686">
                Official Seal
              </p>
              <p className="text-[8px] text-gray-400 mt-1" data-id="element-687">
                Renta Platforms Ltd
              </p>
            </div>

            <div className="text-center" data-id="element-688">
              <div className="h-20 flex items-end justify-center mb-2" data-id="element-689">
                <span className="font-serif italic text-3xl text-blue-900 opacity-80" style={{
                fontFamily: 'cursive'
              }} data-id="element-690">
                  Renta Admin
                </span>
              </div>
              <div className="h-px w-48 bg-gray-400 mx-auto mb-2" data-id="element-691"></div>
              <p className="text-xs text-gray-500 uppercase tracking-widest" data-id="element-692">
                Authorized Signature
              </p>
              <p className="text-[10px] text-gray-400 mt-1" data-id="element-693">
                Issued: {new Date(certificate.issuedAt).toLocaleDateString()}
              </p>
            </div>
          </div>

          {/* Verification Instructions */}
          <div className="mt-8 pt-6 border-t border-gray-200" data-id="element-694">
            <p className="text-xs text-gray-500 text-center leading-relaxed" data-id="element-695">
              <strong data-id="element-696">Verification Instructions:</strong> To verify the
              authenticity of this certificate, scan the QR code above or visit
              renta.ng/verify and enter Certificate ID: {certificate.id}. For
              inquiries, contact verify@renta.ng or call +234 800 RENTA (73682)
            </p>
          </div>
        </div>
      </div>

      {/* Action Bar */}
      <div className="mt-8 flex justify-center space-x-4" data-id="element-697">
        <button className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-full font-medium shadow-lg hover:bg-blue-700 transition-all hover:-translate-y-0.5" data-id="element-698">
          <Download className="w-4 h-4 mr-2" data-id="element-699" />
          Download PDF
        </button>
        <button className="flex items-center px-6 py-3 bg-white text-gray-700 border border-gray-200 rounded-full font-medium shadow-sm hover:bg-gray-50 transition-all" data-id="element-700">
          <Printer className="w-4 h-4 mr-2" data-id="element-701" />
          Print
        </button>
        <button className="flex items-center px-6 py-3 bg-white text-gray-700 border border-gray-200 rounded-full font-medium shadow-sm hover:bg-gray-50 transition-all" data-id="element-702">
          <Share2 className="w-4 h-4 mr-2" data-id="element-703" />
          Share
        </button>
      </div>

      {/* Usage Notice */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-3xl mx-auto" data-id="element-704">
        <h4 className="text-sm font-bold text-blue-900 mb-2 flex items-center" data-id="element-705">
          <FileText className="w-4 h-4 mr-2" data-id="element-706" />
          Accepted For:
        </h4>
        <ul className="grid grid-cols-2 gap-2 text-xs text-blue-800" data-id="element-707">
          <li className="flex items-center" data-id="element-708">
            <CheckCircle className="w-3 h-3 mr-1 text-green-600" data-id="element-709" />
            Bank account opening
          </li>
          <li className="flex items-center" data-id="element-710">
            <CheckCircle className="w-3 h-3 mr-1 text-green-600" data-id="element-711" />
            Visa applications
          </li>
          <li className="flex items-center" data-id="element-712">
            <CheckCircle className="w-3 h-3 mr-1 text-green-600" data-id="element-713" />
            Utility connections
          </li>
          <li className="flex items-center" data-id="element-714">
            <CheckCircle className="w-3 h-3 mr-1 text-green-600" data-id="element-715" />
            Government services
          </li>
          <li className="flex items-center" data-id="element-716">
            <CheckCircle className="w-3 h-3 mr-1 text-green-600" data-id="element-717" />
            Employment verification
          </li>
          <li className="flex items-center" data-id="element-718">
            <CheckCircle className="w-3 h-3 mr-1 text-green-600" data-id="element-719" />
            Credit applications
          </li>
        </ul>
      </div>
    </motion.div>;
}